import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { api } from "../lib/api";

export default function TournamentPage() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  usePageMeta(t ? t.name : "Tournament Results");

  useEffect(() => {
    api
      .get(`/tournaments/${id}`)
      .then(({ data }) => setT(data))
      .catch(() => setError("Tournament not found."));
  }, [id]);

  if (error) return <p className="mx-auto max-w-5xl px-5 py-16 text-neutral-400">{error}</p>;
  if (!t) return <p className="mx-auto max-w-5xl px-5 py-16 text-neutral-500">Loading…</p>;

  const rounds = [...new Set(t.matches.map((m) => m.round))].sort((a, b) => a - b);

  return (
    <div className="mx-auto max-w-5xl px-5 lg:px-10 py-10 lg:py-16" data-testid="tournament-detail">
      <Link to="/results" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 hover:text-primary transition-colors duration-200" data-testid="back-to-results">
        <ArrowLeft className="h-4 w-4" /> All results
      </Link>
      <h1 className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">{t.name}</h1>
      <div className="mt-3 flex flex-wrap gap-4 text-xs font-mono text-neutral-400">
        <span className="uppercase text-primary">{t.category}</span>
        <span>{format(new Date(t.date), "d MMMM yyyy")}</span>
        <span>{t.player_count} players</span>
        <span>{t.round_count} rounds</span>
        <span>{t.ranked ? "Ranked" : "Not ranked"}</span>
      </div>

      <h2 className="mt-10 text-base md:text-lg font-display">Standings</h2>
      <div className="mt-3 border border-white/10 rounded-md overflow-hidden" data-testid="standings-table">
        <div className="grid grid-cols-[48px_1fr_90px_70px] px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-neutral-500 border-b border-white/10 bg-white/[0.03]">
          <div>#</div><div>Player</div><div className="text-right">W/D/L</div><div className="text-right">Pts</div>
        </div>
        {t.players.map((p, i) => (
          <Link
            key={p.player_id}
            to={`/players/${p.player_id}`}
            data-testid={`standing-row-${p.player_id}`}
            className="grid grid-cols-[48px_1fr_90px_70px] items-center px-3 py-2.5 border-b border-white/5 hover:bg-white/[0.04] group transition-colors duration-200"
          >
            <div className="font-mono text-xs text-neutral-500">{i + 1}</div>
            <div className="text-sm truncate group-hover:text-primary transition-colors duration-200">{p.name}</div>
            <div className="text-right font-mono text-xs">
              <span className="text-win">{p.wins}</span>/<span className="text-draw">{p.draws}</span>/
              <span className="text-loss">{p.losses}</span>
            </div>
            <div className="text-right font-mono text-sm text-primary font-bold">{p.points}</div>
          </Link>
        ))}
      </div>

      <h2 className="mt-10 text-base md:text-lg font-display">Match log</h2>
      <div className="mt-3 space-y-6">
        {rounds.map((r) => (
          <div key={r}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Round {r}</div>
            <div className="space-y-1">
              {t.matches
                .filter((m) => m.round === r)
                .map((m, i) => (
                  <div
                    key={`${r}-${i}`}
                    className="flex items-center gap-3 px-3 py-2 border border-white/10 rounded-sm text-sm"
                    data-testid={`match-row-${r}-${i}`}
                  >
                    <span className="font-mono text-[11px] text-neutral-600 w-8">{m.table || "-"}</span>
                    <span className={m.result === "p1" || m.result === "bye" ? "text-win" : "text-neutral-300"}>
                      {m.p1_name}
                    </span>
                    <span className="text-neutral-600 text-xs font-mono">vs</span>
                    <span className={m.result === "p2" ? "text-win" : "text-neutral-300"}>
                      {m.p2_name || "BYE"}
                    </span>
                    <span className="ml-auto font-mono text-[11px] uppercase text-neutral-500">
                      {m.result === "draw" ? "Draw" : m.result === "double_loss" ? "Double loss" : m.result === "bye" ? "Bye" : "Win"}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
